import { Editor } from "@tiptap/core";
import { Selection as PMSelection, TextSelection } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

export type ExperimentalSelectionApi = Selection & {
  getComposedRanges: (root: Node) => Range[];
};

export type Coords = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

/**
 * Test to see if the browser supports the experimental getComposedRange api, which is needed for safari support.
 * @returns
 * @param window
 */
export function doesSupportComposedRangeApi(window: Window): window is Window & ExperimentalSelectionApi {
  // Check if we're specifically on Safari, not just if the API exists
  // Chrome has recently implemented getComposedRanges but doesn't need Safari-specific workarounds
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  try {
    return (
      isSafari &&
      window.getSelection != null &&
      typeof (window.getSelection() as ExperimentalSelectionApi).getComposedRanges === "function"
    ); 
  } catch (error) {
    return false;
  }
}

export function getPositionFromRange(selection: PMSelection): { from: number; to: number } {
  const { ranges } = selection;
  const from = Math.min(...ranges.map(range => range.$from.pos));
  let to = Math.max(...ranges.map(range => range.$to.pos));

  if (doesSupportComposedRangeApi(window) && from === to) {
    // Safari workaround
    const selection = window.getSelection();
    const selectionLength = selection ? selection!.toString().trim().length : 0;
    to = from + selectionLength;
  }

  return { from, to };
}

export function getSelection(editor: Editor, view: EditorView): PMSelection | undefined {
  // Safari workaround for shadowdom selection bug, https://bugs.webkit.org/show_bug.cgi?id=163921, via experimental getComposedRange api
  if (doesSupportComposedRangeApi(window)) {
    try {
      const editorRoot = editor.options.element.getRootNode() as ShadowRoot;
      const ranges = (window.getSelection() as ExperimentalSelectionApi)?.getComposedRanges(editorRoot);
      const range = ranges[0];
  
      if (range == null) {
        return undefined;
      }
  
      return createTiptapSelection(view, range) || undefined; 
    } catch (error) {
      return undefined;
    }
  } else {
    // Support for all other browsers is good here, so we can use the default implementation
    return view.state.selection;
  }
}

function createTiptapSelection(view: EditorView, range: StaticRange): TextSelection | undefined {
  try {
    const from = view.posAtDOM(range.startContainer, range.startOffset);
    const to = view.posAtDOM(range.endContainer, range.endOffset);

    // Check if marks are allowed in the selection range
    let canAcceptMarks = true;
    const nonSelectableNodes = ["nw-note", "nw-validation-box"];

    view.state.doc.nodesBetween(from, to, node => {
      // Safari limited support for marks so selection is not allowed with some of our custom components
      if (
        nonSelectableNodes.includes(node.type.name) ||
        (node.isAtom && !node.isText && !node.isTextblock) ||
        (node.type.spec.selectable != null && !node.type.spec.selectable)
      ) {
        canAcceptMarks = false;
        return false;
      }
    });

    if (!canAcceptMarks) {
      return undefined;
    }

    return TextSelection.create(view.state.doc, from, to);
  } catch (error) {
    return undefined;
  }
}
